import { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, errMsg, CAT_ICON, SRC_ICON, REPEATS, fmtDay, wkday, origin, waUrl } from "../lib/api";
import { useApp } from "../lib/app";
import { PersonChip, Sheet, celebrate } from "./bits";
import { PersonPicker } from "./Review";
import { useT } from "../lib/i18n";

export default function TaskCard({ t, compact }) {
  const tr = useT();
  const { me } = useApp();
  const qc = useQueryClient();
  const [menu, setMenu] = useState(false);
  const [edit, setEdit] = useState(null);
  const [pick, setPick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [decline, setDecline] = useState(null);
  const mine = t.assignee_id === me.me.id;
  const owner = me.admin || t.assigned_by === me.me.id;
  const done = t.status === "done";
  const joinWait = t.assignee?.status === "placeholder";
  const refresh = () => qc.invalidateQueries();
  const askable = mine && !done && t.assigned_by && t.assigned_by !== me.me.id && !t.response;
  const giver = me.members.find((m) => m.id === t.assigned_by);
  const declinedBy = t.response === "declined" && me.members.find((m) => m.id === t.declined_by);
  const respond = async (action, reason = "") => {
    setBusy(true);
    try {
      await api.post(`/tasks/${t.id}/respond`, { action, reason });
      toast.success(action === "accept" ? tr("Accepted 👍 {name} will know.", { name: giver?.name }) : tr("Sent back to {name} 💜", { name: giver?.name }));
      setDecline(null); refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const markDone = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${t.id}/done`);
      celebrate();
      toast.success(mine && !me.admin ? tr("Thank you, {name} 🙏 They'll know it's done.", { name: me.me.name }) : tr("Done! Thank you, {name} 🙏", { name: me.me.name }));
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const nudge = async () => {
    setMenu(false);
    const w = window.open("", "_blank");
    try {
      const { data } = await api.post(`/tasks/${t.id}/nudge`, { origin: origin() });
      if (w) w.location.href = waUrl(data.text); else window.location.href = waUrl(data.text);
    } catch (e) { w?.close(); toast.error(errMsg(e)); }
  };
  const patch = async (body) => {
    try { await api.patch(`/tasks/${t.id}`, body); toast.success(tr("Updated")); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    setMenu(false);
    try { await api.delete(`/tasks/${t.id}`); toast(tr("Removed")); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleCheck = (k) => patch({ checklist: t.checklist.map((c, i) => (i === k ? { ...c, done: !c.done } : c)) });

  return (
    <div data-testid={`task-card-${t.id}`} className={`ul-card p-4 ${done ? "opacity-70" : ""}`}>
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none mt-0.5">{CAT_ICON[t.category] || "🏠"}</span>
        <div className="flex-1 min-w-0">
          <p className={`font-bold leading-snug ${done ? "line-through text-[#6B6680]" : ""}`} data-testid="task-title">{t.title}</p>
          <p className="text-xs text-[#6B6680] mt-0.5">
            {SRC_ICON[t.source] || "⌨️"} {done ? tr("Done") : tr("by {d}", { d: fmtDay(t.due_date) })}{t.repeat !== "none" ? ` · ${tr("repeats")} ${t.repeat === "every_n" ? tr("every {n} days", { n: t.repeat_days }) : tr(t.repeat)}` : ""}
          </p>
          {t.waiting && !done && (
            <p className="text-xs font-bold mt-1" style={{ color: "#B45309" }} data-testid="task-waiting">
              ⏳ {mine ? tr("Waiting · was due {d}", { d: wkday(t.due_date) }) : joinWait ? tr("Waiting for {name} to join", { name: t.assignee.name }) : tr("Waiting on {name} · was due {d}", { name: t.assignee?.name, d: wkday(t.due_date) })}
            </p>
          )}
          {!t.waiting && joinWait && !done && <p className="text-xs font-bold mt-1 text-[#B45309]">⏳ {tr("Waiting for {name} to join", { name: t.assignee.name })}</p>}
          {t.category === "medicine" && <p className="text-xs text-[#8A6000] mt-1">{t.runs_out ? `${tr("Runs out {d}", { d: fmtDay(t.runs_out) })} · ` : ""}{tr("Reminder only · follow your doctor's advice.")}</p>}
          {!compact && t.reason && <p className="text-xs text-[#6B6680] mt-1">{t.reason}</p>}
          {askable && giver && <p className="text-xs font-bold mt-1 text-[#6A5AE0]" data-testid="task-from">{tr("From {name}", { name: giver.name })}</p>}
          {t.response === "accepted" && !mine && <p className="text-xs font-bold mt-1 text-[#15803D]" data-testid="task-accepted">👍 {tr("{name} accepted", { name: t.assignee?.name })}</p>}
          {declinedBy && mine && <p className="text-xs font-bold mt-1 text-[#B45309]" data-testid="task-declined">↩ {tr("{name} couldn't take this", { name: declinedBy.name })}{t.decline_reason ? `: “${t.decline_reason}”` : ""}</p>}
        </div>
        <PersonChip m={t.assignee} />
      </div>
      {!compact && t.checklist?.length > 0 && (
        <div className="mt-3 pl-9 space-y-1">
          {t.checklist.map((c, k) => (
            <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!c.done} disabled={done || !(mine || owner)} onChange={() => toggleCheck(k)} className="w-5 h-5 accent-[#6A5AE0]" />
              <span className={c.done ? "line-through text-[#9C97AE]" : ""}>{c.text}</span>
            </label>
          ))}
        </div>
      )}
      {askable && (
        <div className="flex gap-2 mt-3 pl-9">
          <button data-testid="task-accept-btn" disabled={busy} onClick={() => respond("accept")} className="ul-btn ul-ghost flex-1">{tr("👍 Accept")}</button>
          <button data-testid="task-decline-btn" disabled={busy} onClick={() => setDecline("")} className="ul-btn ul-ghost flex-1">{tr("Can't do it")}</button>
        </div>
      )}
      {!done && (mine || (owner && (!compact || t.waiting || joinWait))) && (
        <div className="flex gap-2 mt-3 pl-9">
          {mine && <button data-testid="task-done-btn" disabled={busy} onClick={markDone} className="ul-btn ul-primary flex-1">{tr("Done ✓")}</button>}
          {!mine && owner && (t.waiting || joinWait) && <button data-testid="task-nudge-btn" onClick={nudge} className="ul-btn ul-wa flex-1">{tr(joinWait ? "Remind on WhatsApp" : "Nudge on WhatsApp")}</button>}
          {owner && !compact && <button data-testid="task-menu-btn" onClick={() => setMenu(true)} className="ul-btn ul-ghost !px-3" aria-label="more"><MoreHorizontal size={20} /></button>}
        </div>
      )}
      <Sheet open={menu} onClose={() => setMenu(false)} testid="task-menu">
        <p className="font-display text-xl font-bold mb-3">{t.title}</p>
        <div className="grid gap-2">
          <button data-testid="task-menu-edit" className="ul-btn ul-ghost" onClick={() => { setMenu(false); setEdit({ title: t.title, due_date: t.due_date, repeat: t.repeat, repeat_days: t.repeat_days }); }}>{tr("✏️ Edit")}</button>
          <button data-testid="task-menu-reassign" className="ul-btn ul-ghost" onClick={() => { setMenu(false); setPick(true); }}>{tr("🔁 Reassign")}</button>
          {!mine && <button data-testid="task-menu-nudge" className="ul-btn ul-wa" onClick={nudge}>{tr("💬 Nudge on WhatsApp")}</button>}
          <button data-testid="task-menu-delete" className="ul-btn" style={{ background: "#FFF1E6", color: "#B45309" }} onClick={del}>{tr("🗑 Delete")}</button>
          {!mine && <p className="text-xs text-center text-[#6B6680]">{tr("Only {name} can mark it done.", { name: t.assignee?.name })}</p>}
        </div>
      </Sheet>
      <Sheet open={!!edit} onClose={() => setEdit(null)} testid="task-edit-sheet">
        {edit && (
          <div className="space-y-3">
            <p className="font-display text-xl font-bold">{tr("Edit task")}</p>
            <input data-testid="task-edit-title" className="ul-input" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
            <input data-testid="task-edit-date" type="date" className="ul-input" value={edit.due_date} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} />
            <select className="ul-input" value={edit.repeat} onChange={(e) => setEdit({ ...edit, repeat: e.target.value })}>
              {REPEATS.map(([v, l]) => <option key={v} value={v}>{tr(l)}</option>)}
            </select>
            {edit.repeat === "every_n" && <input type="number" className="ul-input" value={edit.repeat_days || 30} onChange={(e) => setEdit({ ...edit, repeat_days: Number(e.target.value) })} />}
            <button data-testid="task-edit-save" className="ul-btn ul-primary w-full" onClick={() => { patch(edit); setEdit(null); }}>{tr("Save")}</button>
          </div>
        )}
      </Sheet>
      <Sheet open={decline !== null} onClose={() => setDecline(null)} testid="task-decline-sheet">
        <div className="space-y-3">
          <p className="font-display text-xl font-bold">{tr("Can't do '{title}'?", { title: t.title })}</p>
          <p className="text-sm text-[#6B6680]">{tr("It goes back to {name}. A short reason helps (optional).", { name: giver?.name })}</p>
          <input data-testid="task-decline-reason" className="ul-input" maxLength={200} value={decline || ""} placeholder={tr("e.g. Late meeting that day")} onChange={(e) => setDecline(e.target.value)} />
          <button data-testid="task-decline-send" disabled={busy} className="ul-btn ul-primary w-full" onClick={() => respond("decline", decline)}>{tr("Send back politely")}</button>
        </div>
      </Sheet>
      <PersonPicker open={pick} onClose={() => setPick(false)} members={me.members} onPick={(id) => patch({ assignee_id: id })} />
    </div>
  );
}
